import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Sidebar from "../Sidebar/Sidebar";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import config from "../../config";

export default function AdminManagement() {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarType, setSnackbarType] = useState("success");

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      setRefreshing(true);

      const token = sessionStorage.getItem("token");

      const res = await fetch(`${config.apiBaseUrl}/admin/get`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      if (data.success) {
        setAdmins([...data.admin]);
      } else {
        setAdmins([]);
      }
    } catch (error) {
      setAdmins([]);
    } finally {
      setRefreshing(false);
    }
  };

  const openDeleteDialog = (admin) => {
    setSelectedAdmin(admin);
    setDeleteDialog(true);
  };

  const closeDeleteDialog = () => {
    if (!deleteLoading) {
      setDeleteDialog(false);
      setSelectedAdmin(null);
    }
  };

  const handleDelete = async () => {
    if (!selectedAdmin) return;

    try {
      setDeleteLoading(true);
      const token = sessionStorage.getItem("token");
      const res = await fetch(
        `${config.apiBaseUrl}/admin/delete/${selectedAdmin._id}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await res.json();

      setDeleteDialog(false);
      setSelectedAdmin(null);

      if (res.ok) {
        setSnackbarMsg(data.message || "Admin deleted successfully");
        setSnackbarType("success");
        setSnackbarOpen(true);
        await fetchAdmins();
      } else {
        setSnackbarMsg(data.message || "Failed to delete admin");
        setSnackbarType("error");
        setSnackbarOpen(true);
      }
    } catch (error) {
      setDeleteDialog(false);
      setSelectedAdmin(null);
      setSnackbarMsg("Something went wrong");
      setSnackbarType("error");
      setSnackbarOpen(true);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", background: "#F8FAFC" }}>
      <Sidebar mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />

      <Box component="main" sx={{ flexGrow: 1 }}>
        <Box sx={{ p: { xs: 2, md: 4 } }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={4}
            spacing={2}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              {isMobile && (
                <IconButton
                  onClick={() => setMobileOpen(true)}
                  sx={{
                    border: "1px solid #E5E7EB",
                    borderRadius: "12px",
                  }}
                >
                  <MenuRoundedIcon />
                </IconButton>
              )}

              <Typography variant="h4" fontWeight={800}>
                Admin Management
              </Typography>

              {refreshing && (
                <CircularProgress size={20} sx={{ ml: 1 }} />
              )}
            </Stack>

            <Button
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={() =>
                navigate("/admins/add")
              }
              sx={{
                borderRadius: "12px",
                px: 3,
                py: 1.2,
                textTransform: "none",
                fontWeight: 600,
                boxShadow: "none",
                background: "linear-gradient(135deg,#2563EB 0%,#06B6D4 100%)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg,#1D4ED8 0%,#0891B2 100%)",
                  },
                }}
              >
                Add Admin
              </Button>
          </Stack>

          <Box sx={{ position: "relative" }}>
            {refreshing && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(248,250,252,0.6)",
                  borderRadius: 3,
                }}
              >
                <CircularProgress size={32} />
              </Box>
            )}

            <Box
              sx={{
                opacity: refreshing ? 0.5 : 1,
                pointerEvents: refreshing ? "none" : "auto",
                transition: "opacity 0.15s ease",
              }}
            >

          {isMobile ? (
            <Stack spacing={2}>
              {admins.length > 0 ? (
                admins.map((admin) => (
                  <Paper
                    key={admin._id}
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
                    }}
                  >
                    <Typography fontWeight={700}>
                      {admin.name}
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      {admin.email}
                    </Typography>

                    <Typography variant="body2">
                      ID: {admin.adminId}
                    </Typography>

                    <Box mt={1}>
                      <Chip label={admin.accessLevel} size="small" />
                    </Box>

                    <Stack direction="row" spacing={1} mt={2}>
                      <IconButton
                        color="success"
                        onClick={() => navigate(`/admins/view/${admin._id}`)}
                      >
                        <VisibilityIcon />
                      </IconButton>

                      <IconButton
                        color="error"
                        onClick={() => openDeleteDialog(admin)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </Paper>
                ))
              ) : (
                <Paper sx={{ p: 3, textAlign: "center" }}>
                  <Typography>No admins found</Typography>
                </Paper>
              )}
            </Stack>
          ) : (
            <Paper sx={{ borderRadius: 3 }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Admin ID</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {admins.length > 0 ? (
                      admins.map((admin) => (
                        <TableRow key={admin._id}>
                          <TableCell>{admin.adminId}</TableCell>
                          <TableCell>{admin.name}</TableCell>
                          <TableCell>{admin.email}</TableCell>
                          <TableCell>
                            <Chip label={admin.accessLevel} size="small" />
                          </TableCell>

                          <TableCell align="center">
                            <IconButton
                              color="success"
                              onClick={() =>
                                navigate(`/admins/view/${admin._id}`)
                              }
                            >
                              <VisibilityIcon />
                            </IconButton>

                            <IconButton
                              color="error"
                              onClick={() => openDeleteDialog(admin)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          No admins found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
            </Box>
          </Box>
        </Box>
      </Box>

      <Dialog open={deleteDialog} onClose={closeDeleteDialog}>
        <DialogTitle>Delete Admin</DialogTitle>

        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{" "}
            <strong>{selectedAdmin?.name}</strong>?
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={closeDeleteDialog}
            disabled={deleteLoading}
          >
            Cancel
          </Button>

          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleteLoading}
          >
            {deleteLoading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarType}
          variant="filled"
          sx={{ fontWeight: 600 }}
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}